import { SlideStatement } from "../../../SlideStatement";
import { SlideVerdict } from "../../../SlideVerdict";

// The Wider Play. Slide 3 (The Arc) was merged in here on 2026-09-08 (TPF)
// and deleted: this slide owns the expansion thesis — the factory as the engine, ASO
// as the first vertical, and the sequenced path up through mobile app
// marketing.
//
// The TAM ladder that used to open the slide was cut on 2026-09-09 (TPF): the
// ASO Market slide immediately before this one already sizes the category
// top-down and bottom-up with cited sources, so a second, differently-framed
// ladder competed with it and pushed this slide's only unique argument — the
// SEQUENCE — into the margins.
const RUNGS: Array<{
  step: string;
  name: string;
  // The qualifier that used to hang off the name after an em dash. Split out
  // so the headline word can carry real size and the detail can stay small.
  qualifier?: string;
  what: string;
  rides: string;
  now?: boolean;
}> = [
  {
    step: "1 · Now",
    name: "ASO",
    what: "The listing every install begins at, audited and optimised daily. The data foundation for everything above it.",
    rides: "Live — in revenue, shipping nightly.",
    now: true,
  },
  {
    step: "2 · Next",
    name: "Paid",
    qualifier: "Apple Search Ads & UA briefs",
    what: "The winning keywords, competitor gaps and value-prop framing that fix the listing also write the ad brief and the ASA campaign.",
    rides: "Same data, new artifact — sold to customers we already have.",
  },
  {
    step: "3 · Then",
    name: "Measurement",
    qualifier: "MMP integration",
    what: "Ingest attribution from AppsFlyer / Adjust so every shipped revision traces to installs and revenue.",
    rides: "The foothold to become the ASO-aware measurement layer.",
  },
  {
    step: "4 · Later",
    name: "Creative",
    qualifier: "AI ad assets",
    what: "The asset-generation engine that builds store creatives, pointed at paid-acquisition canvases.",
    rides: "Same rendering pipeline, bigger canvas.",
  },
];

export default function TheWiderPlay() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="The Wider Play">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Our agentic software factory is an engine for TAM expansion.{" "}
            <span className="text-blue-700">
              ASO is the first vertical we carve with it.
            </span>
          </h1>
        </div>
      </SlideStatement>

      {/* Below the rule — the sequenced path up through the vertical. */}
      <div className="md:min-h-0 md:flex-1 md:pt-6">
        <p className="mb-4 text-xl font-medium tracking-tight text-slate-700 md:mb-5 md:text-2xl">
          A vision for expansion across four verticals in mobile app marketing,
          each selling to the existing customer base.
        </p>
        <div className="grid grid-cols-1 items-start gap-2.5 md:grid-cols-4 md:gap-3">
          {RUNGS.map((r) => (
            <div
              key={r.step}
              className={`flex flex-col rounded-xl p-3.5 ring-1 md:p-4 ${
                r.now ? "bg-blue-600 ring-blue-600" : "bg-white ring-slate-200"
              }`}
            >
              <p
                className={`text-[11px] font-bold uppercase tracking-[0.15em] ${
                  r.now ? "text-blue-100" : "text-blue-700"
                }`}
              >
                {r.step}
              </p>
              <p
                className={`mt-1 text-xl font-bold leading-tight tracking-tight md:text-2xl ${
                  r.now ? "text-white" : "text-slate-900"
                }`}
              >
                {r.name}
              </p>
              {r.qualifier ? (
                <p
                  className={`mt-0.5 text-sm font-semibold leading-tight tracking-tight md:text-[15px] ${
                    r.now ? "text-blue-100" : "text-slate-600"
                  }`}
                >
                  {r.qualifier}
                </p>
              ) : null}
              <p
                className={`mt-1.5 text-[11px] leading-snug md:text-xs ${
                  r.now ? "text-blue-50" : "text-slate-600"
                }`}
              >
                {r.what}
              </p>
              <p
                className={`mt-auto pt-2 text-[11px] font-semibold leading-snug md:text-xs ${
                  r.now ? "text-white" : "text-blue-800"
                }`}
              >
                {r.rides}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Row E — why the order holds: the slide's actual argument, and until
          now its most buried element. */}
      <SlideVerdict compact>
        <span className="font-bold">
          The product widens across the $5–15B tooling layer without dramatic
          overhead lift.
        </span>{" "}
        Every rung is an extension of the platform, the customer data, and the
        org we already have, not a new company to fund.
      </SlideVerdict>
    </div>
  );
}

